import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config/api';
import './GymCalendar.css';

function GymCalendar({ targetUserId, reportDates = [] }) {
  const { user } = useAuth();
  const [checkins, setCheckins] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    if (targetUserId) {
      fetchCheckins();
    }
  }, [targetUserId]);

  // Debug: Log report dates when they change
  useEffect(() => {
    if (reportDates && reportDates.length > 0) {
      console.log('GymCalendar - Report dates received:', reportDates.map(r => ({
        id: r.id,
        date: r.report_date,
        formatted: r.report_date ? r.report_date.split('T')[0] : r.report_date
      })));
    }
  }, [reportDates]);

  const fetchCheckins = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/gym-checkins/${targetUserId}`);
      const checkinDates = Array.isArray(response.data) ? response.data : [];
      console.log('GymCalendar - Fetched check-ins from server:', checkinDates);
      console.log('GymCalendar - Setting checkins state to:', checkinDates);
      setCheckins(checkinDates);
      setLoading(false);
      // Force a small delay to ensure state update
      setTimeout(() => {
        console.log('GymCalendar - State should be updated now');
      }, 100);
    } catch (error) {
      console.error('Error fetching check-ins:', error);
      setCheckins([]);
      setLoading(false);
    }
  };

  const handleDateClick = async (e, date) => {
    // Prevent any default behavior (form submission, page reload, etc.)
    e.preventDefault();
    e.stopPropagation();
    
    // Don't allow clicking future dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date > today) return;

    // Don't allow clicking while toggling
    if (toggling) return;

    const dateStr = date.toISOString().split('T')[0];
    console.log('GymCalendar - Date clicked:', dateStr);
    console.log('GymCalendar - Current checkins before toggle:', checkins);

    try {
      setToggling(true);
      console.log('GymCalendar - Toggling check-in for date:', dateStr);
      const response = await axios.post(`${API_BASE_URL}/api/gym-checkins/${targetUserId}`, {
        date: dateStr
      });
      console.log('GymCalendar - Toggle response:', response.data);
      
      // Immediately update local state optimistically
      const isCurrentlyChecked = checkins.includes(dateStr);
      if (isCurrentlyChecked) {
        // Remove from checkins
        setCheckins(prev => {
          const updated = prev.filter(d => d !== dateStr);
          console.log('GymCalendar - Removed check-in, new array:', updated);
          return updated;
        });
      } else {
        // Add to checkins
        setCheckins(prev => {
          const updated = [...prev, dateStr];
          console.log('GymCalendar - Added check-in, new array:', updated);
          return updated;
        });
      }
      
      // Then fetch from server to ensure sync
      await fetchCheckins();
    } catch (error) {
      console.error('Error toggling check-in:', error);
      // Revert optimistic update on error
      await fetchCheckins();
      alert('Error updating check-in. Please try again.');
    } finally {
      setToggling(false);
    }
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get first day of month and number of days
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Create calendar grid - use useMemo to recalculate when reportDates or checkins change
  const calendarDays = useMemo(() => {
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = date.toISOString().split('T')[0];
      // Check if this date is in checkins array - normalize comparison
      const isChecked = Array.isArray(checkins) && checkins.length > 0 && checkins.some(checkinDate => {
        if (!checkinDate) return false;
        // Normalize both dates to YYYY-MM-DD format
        const normalizedCheckin = typeof checkinDate === 'string' 
          ? checkinDate.split('T')[0].split(' ')[0]
          : new Date(checkinDate).toISOString().split('T')[0];
        const matches = normalizedCheckin === dateStr;
        if (matches) {
          console.log(`GymCalendar - Date ${dateStr} is checked! Checkin: ${checkinDate}, Normalized: ${normalizedCheckin}`);
        }
        return matches;
      });
      
      // Check if this date has a report - normalize date strings for comparison
      const hasReport = Array.isArray(reportDates) && reportDates.length > 0 && reportDates.some(r => {
        if (!r || !r.report_date) return false;
        // Handle both date string formats (with or without time)
        const reportDateStr = typeof r.report_date === 'string' 
          ? r.report_date.split('T')[0].split(' ')[0] 
          : new Date(r.report_date).toISOString().split('T')[0];
        const matches = reportDateStr === dateStr;
        return matches;
      });
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const isToday = date.getTime() === today.getTime();
      const isFuture = date > today;
      
      days.push({
        day,
        date,
        dateStr,
        isChecked,
        hasReport,
        isToday,
        isFuture
      });
    }
    
    console.log('GymCalendar - Recalculating calendar days. Checkins:', checkins, 'Count:', checkins.length);
    return days;
  }, [year, month, firstDayOfMonth, daysInMonth, checkins, reportDates]);

  // Calculate stats for current month - fix timezone issues
  const monthCheckins = useMemo(() => {
    return checkins.filter(dateStr => {
      if (!dateStr) return false;
      // Parse date string as YYYY-MM-DD to avoid timezone issues
      const [yearStr, monthStr, dayStr] = dateStr.split('-');
      const checkinYear = parseInt(yearStr);
      const checkinMonth = parseInt(monthStr) - 1; // JavaScript months are 0-indexed
      return checkinYear === year && checkinMonth === month;
    }).length;
  }, [checkins, year, month]);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="gym-calendar-container">
      <div className="calendar-header">
        <h3>Progress Check-In</h3>
        <div className="calendar-nav">
          <button onClick={goToPreviousMonth} className="nav-btn">‹</button>
          <span className="month-year">
            {monthNames[month]} {year}
          </span>
          <button onClick={goToNextMonth} className="nav-btn">›</button>
          <button onClick={goToToday} className="today-btn">Today</button>
        </div>
        <div className="calendar-stats">
          <span className="stat-item">
            <strong>{monthCheckins}</strong> days this month
          </span>
          <span className="stat-item">
            <strong>{checkins.length}</strong> total check-ins
          </span>
        </div>
      </div>

      {loading ? (
        <div className="calendar-loading">Loading calendar...</div>
      ) : (
        <div className="calendar-grid">
          {/* Day headers */}
          <div className="calendar-weekdays">
            {dayNames.map(day => (
              <div key={day} className="weekday-header">{day}</div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="calendar-days">
            {calendarDays.map((dayData, index) => {
              if (!dayData) {
                return <div key={`empty-${index}`} className="calendar-day empty"></div>;
              }

              const className = `calendar-day ${dayData.isChecked ? 'checked' : ''} ${dayData.hasReport ? 'has-report' : ''} ${dayData.isToday ? 'today' : ''} ${dayData.isFuture ? 'future' : ''}`.trim();
              
              // Apply inline styles as fallback to ensure checked dates are visible
              const inlineStyle = dayData.isChecked ? {
                backgroundColor: '#1c4e4f',
                borderColor: '#1c4e4f',
                color: 'white'
              } : {};
              
              return (
                <div
                  key={dayData.dateStr}
                  className={className}
                  style={inlineStyle}
                  onClick={(e) => handleDateClick(e, dayData.date)}
                  title={dayData.isFuture ? 'Cannot check-in future dates' : dayData.hasReport ? 'Has report - Click to toggle check-in' : dayData.isChecked ? 'Click to remove check-in' : 'Click to add check-in'}
                >
                  <span className="day-number" style={dayData.isChecked ? { color: 'white' } : {}}>{dayData.day}</span>
                  {dayData.isChecked && <span className="check-icon">✓</span>}
                  {dayData.hasReport && <span className="report-icon">📋</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="calendar-legend">
        <div className="legend-item">
          <div className="legend-box checked"></div>
          <span>Went to gym</span>
        </div>
        <div className="legend-item">
          <div className="legend-box has-report"></div>
          <span>Has report</span>
        </div>
        <div className="legend-item">
          <div className="legend-box today"></div>
          <span>Today</span>
        </div>
        <div className="legend-item">
          <div className="legend-box future"></div>
          <span>Future date</span>
        </div>
      </div>
    </div>
  );
}

export default GymCalendar;

